import { Component, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ToastService } from './toast.service';

/** Circle-icon background per variant — the pill itself stays light/neutral. */
const ICON_BG: Record<string, string> = {
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
  info: 'bg-accent',
};

/**
 * Standalone port of Consumer's core/components/Toast.tsx `ToastContainer`.
 * Bottom-left stack; each toast is a light rounded pill with a colored status
 * icon + dark bold message (matches the shared design). Auto-dismisses via the
 * ToastService timer (no manual close button, per the design).
 */
@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <div
      class="pointer-events-none fixed bottom-0 left-0 z-[9999] flex flex-col items-start gap-3 p-4 sm:p-6"
    >
      @for (t of toastService.toasts(); track t.id) {
        <div
          class="pointer-events-auto flex w-full max-w-md gap-3 rounded-2xl bg-[#E8E8E8]/95 px-4 py-3.5 shadow-[0_8px_24px_rgba(0,0,0,0.28)] backdrop-blur-md animate-slide-up"
          [class.items-center]="!t.title"
          [class.items-start]="!!t.title"
          [class.py-4]="!!t.title"
          role="alert"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full {{
              iconBgClass(t.variant)
            }}"
            [class.mt-0.5]="!!t.title"
          >
            <svg
              class="h-4 w-4 text-white"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              @switch (t.variant) {
                @case ('success') {
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                }
                @case ('error') {
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                }
                @case ('warning') {
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01" />
                }
                @default {
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8h.01M11 11h1v5" />
                }
              }
            </svg>
          </span>

          <div class="min-w-0 flex-1 pr-1">
            @if (t.title) {
              <p
                class="font-bold text-[#141414]"
                style="font-size:16px;line-height:22px;letter-spacing:-0.005em"
              >
                {{ t.title }}
              </p>
            }
            @if (t.message) {
              <p
                class="font-medium text-[#3D3D3D]"
                [class.mt-1]="!!t.title"
                [class.font-bold]="!t.title"
                [class.text-[#141414]]="!t.title"
                style="font-size:14px;line-height:20px;letter-spacing:-0.005em"
              >
                {{ t.message }}
              </p>
            }
          </div>

          <button
            type="button"
            (click)="toastService.dismiss(t.id)"
            class="hover:cursor-pointer flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#141414]/70 transition-colors hover:bg-black/5 hover:text-[#141414]"
            [class.-mr-1]="!!t.title"
            [class.-mt-1]="!!t.title"
            [attr.aria-label]="'common.toast.dismiss' | translate"
          >
            <svg
              class="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      }
    </div>
  `,
})
export class ToastContainerComponent {
  toastService = inject(ToastService);
  iconBgClass(variant: string): string {
    return ICON_BG[variant] ?? ICON_BG['info'];
  }
}
